import { IResourceList } from 'src/components/challenge/ChallengeInterfaces';
import { CampaignLabelStatus } from 'src/components/challenge/ChallengeResourceWrapper';
import {
  CampaignModuleStatusEnum,
  ResourceTypeDetailEnum,
} from 'src/shared/enums';

const {
  SHOPPING_CART_DESCRIPTION,
  SHOPPING_CART_CONDITIONS,
  SHOPPING_CART_BUTTON_TEXT,
  SHOPPING_CART_MAX_ITEMS,
  SHOPPING_CART_MIN_ITEMS,
  SHOPPING_CART_GROUP_DESCRIPTION,
  SHOPPING_CART_GROUP_MAX_ITEMS,
  SHOPPING_CART_GROUP_MIN_ITEMS,
  SHOPPING_CART_PRODUCT_ID,
  SHOPPING_CART_PRODUCT_MAX_ITEMS,
  SHOPPING_CART_PRODUCT_MIN_ITEMS,
  SHOPPING_CART_PRODUCT_DEFAULT_ITEMS,
} = ResourceTypeDetailEnum;

export interface IShoppingCart {
  buttonText: string;
  conditionsContent: string;
  description: string;
  groups: IShoppingGroup[];
  groupsQuantity?: number;
  isCorrect: boolean;
  isStockCorrect: boolean;
  maxItems?: number;
  minItems?: number;
  name: string;
  points: number;
}

export interface IShoppingGroup {
  isCorrect: boolean;
  maxItems?: number;
  minItems?: number;
  name: string;
  products: IShoppingProduct[];
  productsQuantity?: number;
}

export interface IShoppingProduct {
  description: string;
  id: number;
  idProduct: string;
  idResource: number;
  image?: string;
  isCorrect: boolean;
  maxItems: number;
  minItems: number;
  name: string;
  quantity: number;
  sku: string;
  stock: number;
}

export interface IMagentoProduct {
  description: string;
  id: number;
  image: string;
  name: string;
  sku: string;
  stock: number;
}

export interface IOrderProduct {
  productId: number;
  qty: number;
}

export const buildCartInfo = (
  { name, resourceDetailList = [], resourceList = [], score }: IResourceList,
  shoppingProducts: any
) => {
  const cartInfo = { name, isStockCorrect: true } as IShoppingCart;
  let isCorrect = true;

  resourceDetailList
    .filter(({ status }) => status)
    .forEach(({ idResourceTypeD: { idResourceTypeD }, value }) => {
      switch (idResourceTypeD) {
        case SHOPPING_CART_DESCRIPTION:
          cartInfo.description = value;
          break;
        case SHOPPING_CART_CONDITIONS:
          cartInfo.conditionsContent = value;
          break;
        case SHOPPING_CART_BUTTON_TEXT:
          cartInfo.buttonText = value;
          break;
        case SHOPPING_CART_MAX_ITEMS:
          cartInfo.maxItems = +value;
          break;
        case SHOPPING_CART_MIN_ITEMS:
          cartInfo.minItems = +value;
          break;
        default:
          break;
      }
    });

  cartInfo.groups = resourceList.map((resource: IResourceList) => {
    const [group, stock] = buildGroupInfo(resource, shoppingProducts);
    if (!stock) cartInfo.isStockCorrect = false;
    return group;
  });

  const productsQuantity =
    cartInfo.groups
      .map((group) => {
        if (!group.isCorrect) isCorrect = false;
        return group.productsQuantity;
      })
      .reduce((curr, acc) => curr + acc) || 0;

  if (
    isCorrect &&
    !(
      cartInfo.minItems <= productsQuantity &&
      productsQuantity <= cartInfo.maxItems
    )
  )
    isCorrect = false;

  cartInfo.points = score?.points || 0;

  return { ...cartInfo, isCorrect };
};

export const buildGroupInfo = (
  { name, resourceDetailList = [], resourceList = [] }: IResourceList,
  shoppingProducts
): [IShoppingGroup, boolean] => {
  const groupInfo = { name } as IShoppingGroup;
  let isCorrect = true;
  let outOfStock = false;

  resourceDetailList
    .filter(({ status }) => status)
    .forEach(({ idResourceTypeD: { idResourceTypeD }, value }) => {
      switch (idResourceTypeD) {
        case SHOPPING_CART_GROUP_DESCRIPTION:
          groupInfo.name = value;
          break;
        case SHOPPING_CART_GROUP_MAX_ITEMS:
          groupInfo.maxItems = +value;
          break;
        case SHOPPING_CART_GROUP_MIN_ITEMS:
          groupInfo.minItems = +value;
          break;
        default:
          break;
      }
    });

  const products =
    resourceList.map((resource: IResourceList) => {
      const [product, stock] = buildProductInfo(resource, shoppingProducts);
      if (!stock) outOfStock = true;
      return product;
    }) || [];

  const productsQuantity =
    products
      .map((product) => {
        if (!product.isCorrect) isCorrect = false;
        return product.quantity;
      })
      .reduce((acc, curr) => acc + curr) || 0;

  groupInfo.productsQuantity = productsQuantity;
  if (
    isCorrect &&
    !(
      groupInfo.minItems <= productsQuantity &&
      productsQuantity <= groupInfo.maxItems
    )
  )
    isCorrect = false;

  return [{ ...groupInfo, products, isCorrect }, outOfStock];
};

export const buildProductInfo = (
  { idResource, resourceDetailList }: IResourceList,
  shoppingProducts: IMagentoProduct[]
): [IShoppingProduct, boolean] => {
  const productInfo = { idResource } as IShoppingProduct;
  const isCorrect = true;
  let outOfStock = false;

  resourceDetailList
    .filter(({ status }) => status)
    .forEach(({ idResourceTypeD: { idResourceTypeD }, value }) => {
      switch (idResourceTypeD) {
        case SHOPPING_CART_PRODUCT_ID:
          productInfo.idProduct = value;
          break;
        case SHOPPING_CART_PRODUCT_MAX_ITEMS:
          productInfo.maxItems = +value;
          break;
        case SHOPPING_CART_PRODUCT_MIN_ITEMS:
          productInfo.minItems = +value;
          break;
        case SHOPPING_CART_PRODUCT_DEFAULT_ITEMS:
          productInfo.quantity = +value;
          break;
        default:
          break;
      }
    });

  // TODO comprobar el stock
  const magentoExtraData = shoppingProducts?.find(
    ({ sku }) => productInfo.idProduct === sku
  );

  if (magentoExtraData) {
    magentoExtraData.stock = 100;

    if (magentoExtraData.stock < productInfo.minItems) outOfStock = true;
    if (magentoExtraData.stock < productInfo.maxItems)
      productInfo.maxItems = magentoExtraData.stock;
  }

  return [{ ...productInfo, ...magentoExtraData, isCorrect }, outOfStock];
};

export const modifyProductQuantity = (
  cartInfo: IShoppingCart,
  idResource: number,
  quantity = 0
) => {
  const newCart = { ...cartInfo };

  // set new quantity
  cartInfo.groups.forEach((group) => {
    group.products.forEach((product) => {
      const { idResource: id, maxItems, minItems } = product;
      if (id === idResource && quantity <= maxItems && quantity >= minItems) {
        product.quantity = quantity;
      }
    });
  });

  return validatedShoppingCart(newCart);
};

export const validatedShoppingCart = (
  shoppingInfo: IShoppingCart
): IShoppingCart => {
  let isCorrect = true;

  const totalProducts =
    shoppingInfo.groups
      .map((group) => {
        const groupProducts =
          group.products
            .map((product) => {
              // check product items
              if (!isValidProduct(product)) isCorrect = false;
              return product.quantity;
            })
            .reduce((acc, curr) => acc + curr) || 0;

        // check group items
        group.productsQuantity = groupProducts;
        if (!isValidGroup(group)) isCorrect = false;

        return groupProducts;
      })
      .reduce((acc, curr) => acc + curr) || 0;
  // check total items
  shoppingInfo.groupsQuantity = totalProducts;
  if (!isValidCart(shoppingInfo)) isCorrect = false;

  return { ...shoppingInfo, isCorrect };
};

export const getShoppingCartLabel = ({
  isCompleted,
  isActivator,
  isStockCorrect,
  shoppingCartStatus,
}: {
  isCompleted: boolean;
  isActivator: boolean;
  isStockCorrect: boolean;
  shoppingCartStatus?: CampaignModuleStatusEnum;
}): null | { label: string; style: CampaignLabelStatus } => {
  switch (true) {
    case isCompleted && isActivator:
      return {
        label: 'page.challenge.congratulations-shopping-completed-activator',
        style: 'completed',
      };
    case isCompleted:
      return {
        label: 'page.challenge.congratulations-shopping-completed',
        style: 'completed',
      };
    case !isStockCorrect:
      return {
        label: 'page.challenge.shopping-no-stock',
        style: 'error',
      };
    case !isCompleted && isActivator:
      return {
        label: 'page.challenge.complete-shopping-to-activate',
        style: 'plain',
      };
  }
  return null;
};

export const getOrderProducts = (cart: IShoppingCart): IOrderProduct[] => {
  const orderProducts: IOrderProduct[] = [];
  if (!cart.groups?.length) return;

  cart.groups.forEach(({ products }: IShoppingGroup) => {
    if (!products?.length) return;

    products?.forEach(({ sku, quantity }: IShoppingProduct) => {
      orderProducts.push({ qty: quantity, productId: parseInt(sku) });
    });
  });

  return orderProducts;
};

const isValidProduct = ({ quantity, maxItems, minItems }: IShoppingProduct) => {
  return checkQuantity({ quantity, minItems, maxItems });
};

const isValidGroup = ({
  productsQuantity,
  maxItems,
  minItems,
}: IShoppingGroup) => {
  return checkQuantity({ quantity: productsQuantity, minItems, maxItems });
};

const isValidCart = ({ groupsQuantity, maxItems, minItems }: IShoppingCart) => {
  return checkQuantity({ quantity: groupsQuantity, minItems, maxItems });
};

const checkQuantity = ({
  quantity,
  maxItems,
  minItems,
}: {
  quantity: number | null;
  maxItems: number | null;
  minItems: number | null;
}) => {
  if (quantity === null) return true;
  if (maxItems !== null && quantity > maxItems) return false;
  if (minItems !== null && quantity < minItems) return false;
  return true;
};
